import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircleQuestion } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GROUPS = [
  {
    title: "Ordering & Pricing",
    items: [
      {
        q: "How do I order digital gift cards in bulk?",
        a: "Start with a conversation: tell us your markets, brands, and volumes through the contact form or by emailing raman@vowpin.com. We curate a catalog for your program, share best-offer pricing, and can have your first delivery running within days.",
      },
      {
        q: "How does pricing work?",
        a: "Pricing is tailored to your volume, markets, and product mix — that's why every product in our catalog says \"Ask for Best Offer\". The larger and more consistent your program, the sharper the pricing we can offer.",
      },
      {
        q: "Is there a minimum order size?",
        a: "We support programs of many sizes — from first pilots to enterprise-scale distribution. Share your expected volumes and we'll shape the right starting point together.",
      },
    ],
  },
  {
    title: "Delivery & Fulfillment",
    items: [
      {
        q: "How fast is digital delivery?",
        a: "Digital gift cards, gaming vouchers, and codes are fulfilled within seconds of an order being confirmed. Our fulfillment runs 24/7, so your recipients never wait for business hours.",
      },
      {
        q: "How do recipients receive their gifts?",
        a: "Through branded delivery by email and SMS, with your designs and custom messaging. Every delivery carries your brand, not ours.",
      },
      {
        q: "Can deliveries be scheduled in advance?",
        a: "Yes. Corporate programs can schedule sends for milestones, festivals, onboarding dates, and campaign launches — set once, delivered on time.",
      },
    ],
  },
  {
    title: "Regions & Brands",
    items: [
      {
        q: "Which countries do you cover?",
        a: "We distribute across 120+ countries, with deep regional catalogs for India, the UAE, Saudi Arabia, and the USA — each with its own dedicated catalogue page on this site.",
      },
      {
        q: "Can I get a catalog for a specific region?",
        a: "Absolutely. Browse the regional pages for India, the UAE, Saudi Arabia, and the USA, or request the full catalogue with denominations through the contact form.",
      },
      {
        q: "Which brands are available?",
        a: "500+ global and regional brands across retail, gaming, streaming, travel, dining, mobile, and software — from Amazon, Apple, Steam, and PlayStation to Myntra, Zomato, Noon, and Careem.",
      },
    ],
  },
  {
    title: "Corporate Programs",
    items: [
      {
        q: "Can we put our own branding on the gift cards?",
        a: "Yes — branded designs and custom messaging are part of every corporate program. Your recipients see your brand from the moment the gift lands.",
      },
      {
        q: "Do you support employee rewards and customer loyalty programs?",
        a: "That's our specialty. We power employee recognition, customer loyalty, channel incentives, and promotional campaigns with curated catalogs matched to your audience.",
      },
      {
        q: "Who do I contact for a partnership?",
        a: "For sales enquiries write to raman@vowpin.com; for business partnerships write to sam@vowpin.com. Or use the contact form and we'll route you to the right person.",
      },
    ],
  },
  {
    title: "Trust & Reliability",
    items: [
      {
        q: "How reliable is fulfillment at scale?",
        a: "Vowpin Global runs trusted enterprise fulfillment with consistent delivery across programs of every size. Reliability is the reason businesses choose us for repeat, high-volume gifting.",
      },
      {
        q: "Is Vowpin suitable for enterprise buyers?",
        a: "Yes — we are a B2B-first distributor. Enterprise programs get dedicated catalogs, volume pricing, and a team that understands procurement, compliance reviews, and rollout timelines.",
      },
    ],
  },
];

export default function Faq() {
  useEffect(() => {
    document.title = "FAQ — Vowpin Global | Digital Gift Card Distribution";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "Answers to common questions about digital gift card distribution, bulk ordering, pricing, delivery, regions, and corporate programs at Vowpin Global.");
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: GROUPS.flatMap((g) =>
        g.items.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        }))
      ),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.getElementById("faq-schema")?.remove();
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-24">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#f4a900]/40 bg-[#f4a900]/[0.08] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#b87e00]">
              <MessageCircleQuestion className="h-3.5 w-3.5" />
              FAQ
            </p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
              Questions, <span className="text-gradient-blue">Answered</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#64748b] md:text-lg">
              Everything businesses ask us about digital gift card distribution — ordering, pricing,
              delivery, regions, and corporate programs.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ groups */}
      <section className="border-t border-[#e8ecf4] bg-[#f7f9fc] py-20">
        <div className="mx-auto max-w-4xl space-y-14 px-6 lg:px-10">
          {GROUPS.map((g, gi) => (
            <div key={g.title} data-testid={`faq-group-${gi}`}>
              <Reveal>
                <h2 className="font-display text-2xl font-semibold text-[#111827]">{g.title}</h2>
              </Reveal>
              <Reveal delay={0.08}>
                <Accordion type="single" collapsible className="mt-6 space-y-3">
                  {g.items.map((item, i) => (
                    <AccordionItem
                      key={item.q}
                      value={`${gi}-${i}`}
                      className="glow-card rounded-2xl border border-[#e8ecf4] px-6"
                      data-testid={`faq-item-${gi}-${i}`}
                    >
                      <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-[#111827] hover:text-[#0816a8] hover:no-underline">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 text-sm leading-relaxed text-[#64748b] md:text-base">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          ))}

          <Reveal>
            <div className="glow-card flex flex-wrap items-center justify-between gap-5 rounded-[20px] p-8">
              <div>
                <h3 className="font-display text-xl font-semibold text-[#111827]">Still have a question?</h3>
                <p className="mt-2 text-sm text-[#64748b]">
                  Our team replies within one business day.
                </p>
              </div>
              <Link
                to="/contact"
                data-testid="faq-talk-to-sales-btn"
                className="group flex items-center gap-2 rounded-full bg-[#0816a8] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_-8px_rgba(8,22,168,0.5)] transition-all duration-300 hover:bg-[#0a1bc4]"
              >
                Talk to Sales
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
